import { useEffect, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import * as echarts from 'echarts/core'
import { BarChart } from 'echarts/charts'
import { GridComponent, TooltipComponent } from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'
import { listTriggers } from '../../../api/triggers'
import { QueryBoundary } from '../../../components/common/QueryBoundary'
import { Skeleton } from '../../../components/ui/skeleton'

echarts.use([BarChart, GridComponent, TooltipComponent, CanvasRenderer])

/** 24h 触发时序柱状图（echarts/core 按需注册） */
export function TriggerTimelineChart() {
  const containerRef = useRef<HTMLDivElement>(null)
  const chartRef = useRef<echarts.ECharts | null>(null)

  const query = useQuery({
    queryKey: ['triggers', { chart: '24h' }],
    queryFn: () => listTriggers({ page: 1, pageSize: 100 }),
    refetchInterval: 60_000,
    retry: 1,
  })

  // 创建实例与更新合并：StrictMode 双挂载后 getInstanceByDom 幂等重建
  useEffect(() => {
    const el = containerRef.current
    const triggers = query.data?.items
    if (!el) return
    const chart = echarts.getInstanceByDom(el) ?? echarts.init(el)
    chartRef.current = chart
    if (!triggers) return
    const buckets = buildHourBuckets(triggers)
    chart.setOption({
      grid: { left: 34, right: 10, top: 12, bottom: 22 },
      tooltip: {
        trigger: 'axis',
        backgroundColor: '#0d1420',
        borderColor: 'rgba(148,163,196,.2)',
        textStyle: { color: '#e8eef7', fontSize: 11 },
      },
      xAxis: {
        type: 'category',
        data: buckets.labels,
        axisLine: { lineStyle: { color: 'rgba(148,163,196,.2)' } },
        axisTick: { show: false },
        axisLabel: { color: '#8b98ab', fontSize: 9, interval: 3 },
      },
      yAxis: {
        type: 'value',
        minInterval: 1,
        splitLine: { lineStyle: { color: 'rgba(148,163,196,.08)' } },
        axisLabel: { color: '#8b98ab', fontSize: 9 },
      },
      series: [
        {
          type: 'bar',
          data: buckets.values,
          itemStyle: { color: '#56d7ff', opacity: 0.75, borderRadius: [2, 2, 0, 0] },
          barWidth: '60%',
        },
      ],
    })
  }, [query.data])

  // 卸载时销毁实例
  useEffect(() => () => {
    if (containerRef.current) echarts.dispose(containerRef.current)
    chartRef.current = null
  }, [])

  return (
    <div className="panel-glass flex flex-col rounded-xl">
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <h2 className="text-sm font-semibold text-foreground">触发时序（24h）</h2>
        <span className="text-[10px] text-muted-foreground">最近 100 条</span>
      </div>
      <QueryBoundary
        query={query}
        skeleton={<Skeleton className="m-3 h-[120px] rounded-lg" />}
        isEmpty={(d) => d.items.length === 0}
        empty={{ title: '暂无触发数据' }}
      >
        {() => <div ref={containerRef} className="h-[132px] w-full" aria-label="触发时序图" role="img" />}
      </QueryBoundary>
    </div>
  )
}

function buildHourBuckets(triggers: Array<{ createdAt: number }>): { labels: string[]; values: number[] } {
  const now = new Date()
  const labels: string[] = []
  const values: number[] = []
  const counts = new Map<number, number>()
  for (const t of triggers) {
    const hour = Math.floor(t.createdAt / 3600_000)
    counts.set(hour, (counts.get(hour) ?? 0) + 1)
  }
  for (let i = 23; i >= 0; i--) {
    const d = new Date(now.getTime() - i * 3600_000)
    labels.push(`${d.getHours()}:00`)
    values.push(counts.get(Math.floor(d.getTime() / 3600_000)) ?? 0)
  }
  return { labels, values }
}

